import {
  AloftPrice,
  CraftsmanAdjustmentProps,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
  ORDER_FEATURES,
  schPositiveInt,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence, selectCompTypes } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSubjectDepreciationRatioForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';

const calculateBelowGradeFullBathsAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatio: number | undefined,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;

  if (compType === 'arv') {
    subjectImprovements = improvements.subjectArv;
  }
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatio) ||
    isUndefined(subjectPropertyIdentification)
  ) {
    return undefined;
  }

  const {
    basement,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = subjectImprovements;
  const { basement: compBasement } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;

  const subjBelowGradeFullBaths = ORDER_FEATURES.basement.hasFeature(subjectImprovements) && basement?.fullBaths ? basement?.fullBaths : schPositiveInt.parse(0);
  const compBelowGradeFullBaths = ORDER_FEATURES.basement.hasFeature(compImprovements) && compBasement?.fullBaths ? compBasement?.fullBaths : schPositiveInt.parse(0);

  if (isUndefined(quality) || (quality * 10) % 1 !== 0 || quality < 1 || isUndefined(zipCode)) {
    return undefined;
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  return adjCalculator.getFullBathsAdj({
    subject: {
      fullBaths: subjBelowGradeFullBaths,
      quality,
      propertyType,
      zipCode,
    },
    comp: { fullBaths: compBelowGradeFullBaths },
    depreciationRatio,
  }).adjustment;
};

export const selectBelowGradeFullBathsAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    (state, id: string) => id,
    selectCompTypes,
  ],
  calculateBelowGradeFullBathsAdj,
);

export const selectBelowGradeFullBathsAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectCompSequence,
    selectCompTypes,
  ],
  (adjustmentProps, improvements, propertyIdentification, depreciationRatio, compSequence, compTypes) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateBelowGradeFullBathsAdj(
        adjustmentProps,
        improvements,
        propertyIdentification,
        depreciationRatio,
        propID,
        compTypes,
      );
      if (isDefined(adj)) acc[propID] = adj;
      return acc;
    }, {}),
);
